
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { BookOpen, CheckCircle, Play, Layers } from "lucide-react";
import { cn } from "@/lib/utils";

interface Module {
  id: string;
  title: string;
  description: string;
  order_index: number;
}

interface ModuleProgress {
  totalLessons: number;
  completedLessons: number;
  progressPercentage: number;
}

interface ModuleCardProps {
  module: Module;
  progress?: ModuleProgress;
  onClick: () => void;
}

/**
 * ModuleCard component - displays individual module with progress information
 * Shows completion status, progress bar, and lesson counts
 */
export const ModuleCard = ({ module, progress, onClick }: ModuleCardProps) => {
  // Calculate progress values with fallbacks
  const progressPercentage = progress?.progressPercentage || 0;
  const completedLessons = progress?.completedLessons || 0;
  const totalLessons = progress?.totalLessons || 0;
  
  // Determine status based on progress
  const isCompleted = progressPercentage === 100;
  const isInProgress = progressPercentage > 0 && progressPercentage < 100;
  const isNotStarted = progressPercentage === 0;

  console.log("ModuleCard progress data:", {
    moduleId: module.id,
    title: module.title,
    progressPercentage,
    completedLessons,
    totalLessons,
    isCompleted,
    isInProgress,
    isNotStarted
  });

  // Get status info for display
  const getStatusInfo = () => {
    if (isCompleted) {
      return { text: "Completed", color: "text-green-600", bgColor: "bg-green-50", icon: CheckCircle };
    }
    if (isInProgress) {
      return { text: "In Progress", color: "text-blue-600", bgColor: "bg-blue-50", icon: Play };
    }
    return { text: "Start Learning", color: "text-gray-600", bgColor: "bg-gray-50", icon: BookOpen };
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  return (
    <Card 
      onClick={onClick}
      className={cn(
        "group relative transition-all duration-300 border-gray-100/50 min-h-[200px]",
        "hover:shadow-lg hover:scale-[1.02] cursor-pointer",
        isCompleted ? "bg-gradient-to-br from-green-50 to-emerald-50" :
        isInProgress ? "bg-gradient-to-br from-blue-50 to-indigo-50" :
        "bg-gradient-to-br from-white to-gray-50"
      )}
    >
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg transition-transform duration-300",
              isCompleted ? "bg-green-100" : 
              isInProgress ? "bg-blue-100" :
              "bg-gray-100",
              "group-hover:scale-110"
            )}>
              <StatusIcon className={cn(
                "h-5 w-5",
                isCompleted ? "text-green-600" :
                isInProgress ? "text-blue-600" :
                "text-gray-600"
              )} />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg leading-tight">
                {module.title}
              </CardTitle>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600 line-clamp-2">
          {module.description}
        </p>
        
        {/* Progress Section */}
        <div className="space-y-3">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-500">Progress</span>
              <span className="font-medium text-gray-700">{progressPercentage}%</span>
            </div>
            <Progress 
              value={progressPercentage} 
              className="h-2"
            />
          </div>
          
          {/* Lesson Count and Status */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {totalLessons > 0 ? `${completedLessons}/${totalLessons} lessons` : 'No lessons'}
            </span>
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
              statusInfo.bgColor,
              statusInfo.color
            )}>
              {statusInfo.text}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
